import { Anchor, Badge, Button, Stack, Tabs, Text, Title } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { rawApi } from '../api/client';
import { listKey } from '../api/queryKeys';
import { CrudSection } from '../components/CrudSection';
import { useIslandToday, useLocationId } from '../hooks/useSeedLocation';
import { useTabInUrl } from '../hooks/useTabInUrl';
import { enumLabel, enumOptions } from '../lib/enums';
import { formatDate, formatMoney, formatRateBp, toIsoDate } from '../lib/format';
import { notifyDone, notifyError } from '../lib/notify';
import { PayoutPaymentsSection } from './referrals/PayoutPaymentsSection';
import { PayoutsSection } from './referrals/PayoutsSection';
import { PayPartnerButton, type ReferralBalance } from './referrals/PayPartnerButton';
import { PARTNER_FIELDS, PROFILES_PATH } from './referrals/partner';
import { UnderTheirMinimum } from './referrals/UnderTheirMinimum';
import { WhatTheNightlyLeftOut } from './referrals/WhatTheNightlyLeftOut';
import { WorkOutCommissionSection } from './referrals/WorkOutCommissionSection';

const BALANCES_PATH = '/admin/v1/referrals/balances';

export function ReferralsPage() {
  const locationId = useLocationId();
  const islandToday = useIslandToday();
  const queryClient = useQueryClient();
  // In the address, so the dashboard's "Commission owed" tile can land on the money rather than on
  // the list of partners.
  const [tab, setTab] = useTabInUrl(
    ['profiles', 'balances', 'payouts', 'plans', 'accruals'] as const,
    'profiles',
  );

  /**
   * The same request the balances table makes, under the same key, so reading it here costs nothing.
   *
   * A partner owed both lempiras and dollars has a row for each, and paying one of them must say so
   * about the other — otherwise his row disappearing reads as everything being settled.
   */
  const balances = useQuery({
    queryKey: listKey(BALANCES_PATH),
    queryFn: () => rawApi<ReferralBalance[]>('GET', BALANCES_PATH),
  });

  const createRun = async () => {
    if (!locationId) return;
    // THE BAR'S DAY, not the reader's. This had been half-fixed: local calendar parts instead of
    // `toISOString()`, with a comment reasoning correctly about Honduras being six hours behind
    // UTC — and then read off whatever browser happened to be open. `periodStart` and `periodEnd`
    // are STORED on the payout run, so a run started from a laptop in another country covers a
    // period the bar never traded, and the envelope a driver is handed says so.
    const today = new Date(`${islandToday}T12:00:00`);
    const periodStart = toIsoDate(new Date(today.getFullYear(), today.getMonth(), 1));
    const periodEnd = islandToday;
    try {
      const result = await rawApi<{ lines: { profileId: string }[] }>(
        'POST',
        '/admin/v1/referrals/payout-runs',
        { locationId, periodStart, periodEnd },
      );
      // Somebody owed both lempiras and dollars gets an envelope for each, so the lines are counted
      // by person: "2 partners are on this month's list" has to mean two people.
      const count = new Set(result.lines.map((l) => l.profileId)).size;
      notifyDone(
        'Payout list ready',
        `${count} ${count === 1 ? 'partner is' : 'partners are'} on this month's list, from the 1st to today. Open Payouts to hand the money over.`,
      );
      void queryClient.invalidateQueries({ queryKey: listKey(BALANCES_PATH) });
      // The new list is the point of the button, so the tab that shows it must not be holding a
      // cached answer from before it existed.
      void queryClient.invalidateQueries({ queryKey: listKey('/admin/v1/referrals/payout-lines') });
      void queryClient.invalidateQueries({ queryKey: listKey('/admin/v1/referrals/payout-runs') });
    } catch (err) {
      notifyError(err, "start this month's payout");
    }
  };

  return (
    <>
      <Title order={3} mb="md">
        Taxi &amp; tour partners
      </Title>
      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="profiles">Partners</Tabs.Tab>
          <Tabs.Tab value="balances">What we owe</Tabs.Tab>
          <Tabs.Tab value="payouts">Payouts</Tabs.Tab>
          <Tabs.Tab value="plans">Commission plans</Tabs.Tab>
          <Tabs.Tab value="accruals">Commission history</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="profiles">
          {/*
            A list, and only a list: find a partner, see the two or three facts worth scanning, open
            them. The Edit button that used to sit on every row opened a dialog editing the same
            twenty fields the partner's own page now owns — so a bank account number had two homes
            and no way to tell which one somebody had used. The form below adds a partner who is not
            here yet; everything after that happens on their page.
          */}
          <CrudSection
            title="Drivers and agencies"
            entityNoun="partner"
            description="Everyone who brings guests in and earns commission. Open someone to see what they are owed, what they have earned, and to pay them."
            listPath={PROFILES_PATH}
            defaults={{ locationId }}
            rowLabel={(r) => String(r.name ?? 'this partner')}
            emptyMessage="No partners yet. Add the drivers and agencies who send you guests."
            columns={[
              {
                accessor: 'name',
                title: 'Name',
                // Someone who has stopped bringing guests stays on file — their past commission
                // still has to read back as a name — but they drop out of "what we owe" entirely,
                // so a row that looks identical to everyone else's is how you end up waiting for a
                // balance that will never appear.
                render: (r) => (
                  <Anchor
                    component={Link}
                    to={`/referrals/partners/${String(r.id)}`}
                    fw={600}
                    c={r.active === false ? 'dimmed' : undefined}
                  >
                    {String(r.name ?? '—')}
                    {r.active === false ? ' · no longer working with you' : ''}
                  </Anchor>
                ),
              },
              { accessor: 'type', title: 'Kind', enumGroup: 'referralPartyKind' },
              { accessor: 'parentId', title: 'Drives for', reference: 'referralProfile' },
              { accessor: 'preferredPayoutType', title: 'Paid by', enumGroup: 'payoutMethod' },
              { accessor: 'commissionPlanId', title: 'Plan', reference: 'commissionPlan' },
              {
                accessor: 'bankDetailsVerified',
                title: 'Bank account',
                // Only says anything about someone paid by transfer; a partner paid in cash has no
                // account to check, and an orange badge there would send people looking for a
                // problem that does not exist.
                render: (r) =>
                  r.preferredPayoutType === 'transfer' ? (
                    <Badge color={r.bankDetailsVerified ? 'teal' : 'orange'} variant="light">
                      {r.bankDetailsVerified ? 'Checked' : 'Needs checking'}
                    </Badge>
                  ) : null,
              },
            ]}
            fields={PARTNER_FIELDS}
          />
        </Tabs.Panel>

        <Tabs.Panel value="balances">
          {/*
            Above the balances, because it is what fills them. The figures below used to arrive only
            by somebody working a percentage out on a calculator and typing it in, so an empty "what
            we owe" meant either a quiet week or a job nobody had got round to, and there was no way
            to tell from the screen.
          */}
          {/*
            And above THAT, what last night's run could not work out at all. The commission is
            rebuilt unattended every night; the tables it had to leave — an untagged tab, a partner
            nobody has put on a plan — earned nothing and said so only in a log line. It renders
            nothing on the nights that left nothing out, which is most of them.
          */}
          <WhatTheNightlyLeftOut />
          <WorkOutCommissionSection />
          <div style={{ height: 'var(--mantine-spacing-md)' }} />
          {/*
            Who is carrying over, before the table of everybody. A driver stuck below his minimum
            for a month looks exactly like everybody else on the list below, and he is the one who
            quietly stops bringing guests. It renders nothing at all when nobody is short.
          */}
          <UnderTheirMinimum />
          <CrudSection
            title="What we owe"
            // The last sentence is the one that was missing, and it belongs beside the figures
            // rather than behind anything: an unpaid commission is provisional. Working it out
            // again restates it, a refunded bill takes it back and a volume tier lifts it, so a
            // driver told a figure on Tuesday can be handed a different one on Friday. Said as
            // what is true today — nothing here promises a figure that refreshes on its own.
            description="Commission earned and not yet paid. Cash can be handed over on request; a transfer waits until the partner reaches the minimum you set for them. None of it is final until it is paid — working the commission out again can move it."
            listPath={BALANCES_PATH}
            emptyMessage="Nobody is owed commission right now."
            /*
             * A balance is one partner in one currency, and it carries no id of its own. Without
             * one every row gets the same key, so a partner owed both lempiras and dollars has two
             * rows the table cannot tell apart — and the payment dialog opened from one of them
             * would sit on the other.
             */
            extractList={(data) =>
              (Array.isArray(data) ? (data as Record<string, unknown>[]) : []).map((row) => ({
                ...row,
                id: `${String(row.profileId)}|${String(row.currency)}`,
              }))
            }
            actions={
              <Button size="xs" variant="light" onClick={createRun}>
                Start this month&apos;s payout
              </Button>
            }
            /*
             * Paying the one person who is asking, from the row that says what he is owed. The
             * only way to do it before was "Start this month's payout", which sweeps every partner
             * into a run — six people with a line each that somebody then has to settle or explain
             * — and then sends you to a different tab to find his among theirs. That button stays,
             * because paying everybody at month end is a real job; it is no longer the only one.
             */
            rowActions={(row) => {
              const balance = row as unknown as ReferralBalance;
              return (
                <PayPartnerButton
                  balance={balance}
                  alsoOwed={(balances.data ?? []).filter(
                    (b) => b.profileId === balance.profileId && b.currency !== balance.currency,
                  )}
                />
              );
            }}
            columns={[
              {
                accessor: 'profileName',
                title: 'Partner',
                // Their name is the way into their record — what they have earned, what they have
                // been paid, and how they want to be paid, all of which used to live on four tabs
                // none of which was keyed to a person.
                render: (r) => (
                  <Anchor component={Link} to={`/referrals/partners/${String(r.profileId)}`} fw={600}>
                    {String(r.profileName ?? '—')}
                  </Anchor>
                ),
              },
              { accessor: 'accruedMinor', title: 'Owed', unit: 'money' },
              {
                /*
                 * WHAT HE IS ABOUT TO PAY FOR, beside how much.
                 *
                 * The figure on its own answers "how much" and leaves "for what" to memory. With a
                 * volume tier they are one question: the period is the window the threshold is
                 * counted in, so the amount moves when it is crossed and the window is what decides
                 * which tabs moved with it. An em dash where nobody has agreed a cadence — that
                 * partner is settled on what is outstanding rather than on a calendar, and a period
                 * printed for him would be this screen inventing the fact it is reporting.
                 *
                 * Nothing is due on the payout date and nothing runs on it. It is the morning he
                 * said he would settle, so he can see it coming.
                 */
                accessor: 'period',
                title: 'Period',
                render: (r) => {
                  const period = (r as unknown as ReferralBalance).period;
                  if (!period) return <Text c="dimmed">—</Text>;
                  return (
                    <Stack gap={0}>
                      <Text size="sm">
                        {formatDate(period.periodStart)} – {formatDate(period.periodEnd)}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {enumLabel('payoutCadence', period.cadence)} · pay {formatDate(period.payoutDate)}
                      </Text>
                    </Stack>
                  );
                },
              },
              { accessor: 'preferredPayoutType', title: 'Paid by', enumGroup: 'payoutMethod' },
              {
                accessor: 'payable',
                title: 'Status',
                render: (r) =>
                  r.payable ? (
                    <Badge color="teal" variant="light">
                      Ready to pay
                    </Badge>
                  ) : (
                    <Text size="xs" c="orange">
                      {formatMoney(r.shortfallMinor as number, r.currency as string)} short of their minimum —
                      carries over
                    </Text>
                  ),
              },
            ]}
          />
        </Tabs.Panel>

        <Tabs.Panel value="payouts">
          <PayoutsSection />
          {/* Under the envelopes, because what was actually counted out is the answer to the
              question the envelopes raise — and because a payment made in error is only ever
              found by somebody who came to look at what a partner is still owed. */}
          <PayoutPaymentsSection />
        </Tabs.Panel>

        <Tabs.Panel value="plans">
          <CrudSection
            title="Commission plans"
            entityNoun="plan"
            description="What a partner earns: a share of the bill, or a set amount for every table they send. Volume tiers can lift every driver's share once their agency passes a monthly total, and pay the agency a bonus on top."
            listPath="/admin/v1/referrals/plans"
            createPath="/admin/v1/referrals/plans"
            updatePathTemplate="/admin/v1/referrals/plans/{id}"
            extractList={(d) =>
              (d as { plan: Record<string, unknown>; tiers: unknown[] }[]).map((x) => ({
                ...x.plan,
                tiers: x.tiers,
                tierCount: x.tiers.length,
              }))
            }
            defaults={{ locationId }}
            rowLabel={(r) => String(r.name ?? 'this plan')}
            emptyMessage="No commission plans yet. Add one, then put your partners on it."
            columns={[
              { accessor: 'name', title: 'Plan' },
              {
                // One answer instead of three columns. A plan pays a percentage or a set amount,
                // never both, so "Pays: percentage of the bill · Share: 10% · Per table: L 0.00"
                // spent two columns saying nothing and one of them said it in money.
                accessor: 'kind',
                title: 'They earn',
                noWrap: true,
                render: (r) =>
                  r.kind === 'fixedPerTicket'
                    ? `${formatMoney(r.fixedMinor as number, (r.fixedCurrency as string) ?? 'HNL')} a table`
                    : `${formatRateBp(r.rateBp as number)} of the bill`,
              },
              { accessor: 'basis', title: 'Worked out on', enumGroup: 'referralBase' },
              {
                // In a bar, drinks are most of the bill — a plan that leaves alcohol out pays a
                // fraction of what the same percentage pays on the plan beside it, and that was
                // only visible by opening each one.
                accessor: 'excludeAlcohol',
                title: 'Leaves out',
                render: (r) => {
                  const left = [
                    r.excludeAlcohol ? 'Alcohol' : null,
                    r.excludeDiscounts ? 'Discounts' : null,
                  ].filter(Boolean);
                  return left.length ? (
                    left.join(', ')
                  ) : (
                    <Text size="sm" c="dimmed">
                      Nothing
                    </Text>
                  );
                },
              },
              { accessor: 'effectiveFrom', title: 'Starts', unit: 'date' },
              {
                accessor: 'tierCount',
                title: 'Volume tiers',
                render: (r) => {
                  const count = typeof r.tierCount === 'number' ? r.tierCount : 0;
                  return count === 0 ? (
                    <Text size="sm" c="dimmed">
                      None
                    </Text>
                  ) : (
                    `${count} ${count === 1 ? 'tier' : 'tiers'}`
                  );
                },
              },
            ]}
            fields={[
              { name: 'name', label: 'Plan name', type: 'text', required: true },
              {
                name: 'kind',
                label: 'How it pays',
                type: 'select',
                required: true,
                options: enumOptions('referralRateKind'),
              },
              {
                name: 'rateBp',
                label: 'Share of the bill',
                type: 'number',
                unit: 'percent',
                description: 'Used when the plan pays a percentage',
              },
              {
                name: 'fixedMinor',
                label: 'Amount per table',
                type: 'number',
                unit: 'money',
                description: 'Used when the plan pays a set amount instead of a percentage',
              },
              {
                name: 'fixedCurrency',
                label: 'Currency of that amount',
                type: 'select',
                options: enumOptions('currency'),
              },
              {
                name: 'basis',
                label: 'Worked out on',
                type: 'select',
                options: enumOptions('referralBase'),
              },
              {
                name: 'excludeAlcohol',
                label: 'Leave alcohol out',
                type: 'boolean',
                description:
                  'Anything marked as alcohol on the menu earns no commission. Mark an item as alcohol on its own page — a virgin cocktail sits with the cocktails and still earns.',
              },
              {
                /*
                 * This switch said the opposite of what the plans table beside it says, and of what
                 * the commission is now actually worked out on: the form called it "Ignore discounts
                 * … worked out on the price before any discount comes off", while the table listed
                 * it under "Leaves out: Discounts". One of the two was going to send a driver home
                 * with the wrong money, and since nothing read the column nobody could find out
                 * which.
                 *
                 * ON is what every plan starts with and what the spec has always called the default:
                 * commission on what the guest actually paid. The wording now says which way round
                 * it is in BOTH positions, because a switch whose label describes only one of its
                 * two states is half a label.
                 */
                name: 'excludeDiscounts',
                label: 'Take discounts off first',
                type: 'boolean',
                description:
                  'On: commission is worked out on what the guest was actually charged. Off: a table with 20% off still earns commission on the full menu price.',
              },
              {
                name: 'settlementCurrency',
                label: 'Always pay them in',
                type: 'select',
                options: enumOptions('currency'),
                description:
                  'Leave this empty and a dollar table earns dollars while a lempira table earns lempiras. Set it and every table is converted at the exchange rate that was on the bill that night.',
              },
              {
                name: 'effectiveFrom',
                label: 'Recorded from',
                type: 'date',
                required: true,
                fixedAfterCreate: true,
                description:
                  'The date this agreement was recorded. Changing rates works unpaid commission out again; commission already put on a payout never moves.',
              },
              {
                name: 'tiers',
                label: 'Better rates for more business',
                type: 'rows',
                rowNoun: 'tier',
                description:
                  'Optional. Once an agency’s drivers have brought in this much between them, they all earn the higher share, and the agency itself can earn a bonus on top. “Counted over” is the stretch of trade that decides it: their payout period, the calendar month, or the last 30 days — and somebody with no agreed payout period is counted on everything they have not been paid for yet.',
                rowFields: [
                  {
                    name: 'thresholdMinor',
                    label: 'Once they pass',
                    type: 'number',
                    unit: 'money',
                  },
                  {
                    name: 'thresholdCurrency',
                    label: 'Currency',
                    type: 'select',
                    defaultValue: 'HNL',
                    enumGroup: 'currency',
                  },
                  {
                    name: 'period',
                    label: 'Counted over',
                    type: 'select',
                    defaultValue: 'calendarMonth',
                    options: [
                      /*
                       * First, because it is the one that matches how a partner is settled — and
                       * it no longer means what this option used to say.
                       *
                       * "Since they were last paid", with "paying somebody closes their period and
                       * the count starts over" under it, was true when the window was open at the
                       * front and a payment was the only thing that closed it. For a partner with
                       * an agreed payout period the definition runs the other way round: the
                       * calendar draws the period, and handing him cash inside it changes nothing
                       * about the count. Leaving the old label on the picker the owner chooses
                       * from would have him choose it for a reason it stopped being true for.
                       *
                       * Partners with no agreed period keep the old behavior, which is why the
                       * label says "their payout period" rather than naming a week or a month.
                       */
                      { value: 'sinceLastPayout', label: 'Their payout period' },
                      { value: 'calendarMonth', label: 'The calendar month' },
                      { value: 'rolling30', label: 'The last 30 days' },
                    ],
                  },
                  { name: 'driverRateBp', label: 'Drivers then earn', type: 'number', unit: 'percent' },
                  { name: 'agencyBonusRateBp', label: 'Agency bonus', type: 'number', unit: 'percent' },
                ],
              },
            ]}
          />
        </Tabs.Panel>

        <Tabs.Panel value="accruals">
          <CrudSection
            title="Commission history"
            description={
              // What this said before — "voids and refunds take the commission back off on their own"
              // — is not what happens. Recomputing drops an UNPAID accrual for a voided bill and
              // nothing reverses a PAID one (REF-04 is still open, and `docs/track-1-status.md`
              // records it). A screen that says the system handles something it does not handle is
              // how money walks out the door with nobody looking for it.
              'Every bill tagged to a partner and what it earned them. A bill voided before the commission has been paid drops off the next time you work commission out; one that has already been paid stays, and is settled by taking it off the next payout.'
            }
            listPath="/admin/v1/referrals/accruals"
            /*
             * The rows are a PAGE — the most recent five hundred — and the totals that come with
             * them cover every accrual there is. Nothing on this tab adds a column up, so the page
             * is all it needs; the partner's own screen is where a total is read, and that one
             * takes the server's figure rather than adding up what it was sent.
             */
            extractList={(data) =>
              ((data as { rows?: Record<string, unknown>[] })?.rows ?? []) as Record<string, unknown>[]
            }
            emptyMessage="No commission earned yet. It appears here as soon as a bill is tagged to a partner."
            columns={[
              { accessor: 'at', title: 'When', unit: 'datetime', noWrap: true },
              { accessor: 'profileId', title: 'Partner', reference: 'referralProfile' },
              { accessor: 'level', title: 'Earned by', enumGroup: 'referralLevel' },
              { accessor: 'baseMinor', title: 'Bill', unit: 'money', currencyAccessor: 'baseCurrency' },
              { accessor: 'rateBp', title: 'Share', unit: 'percent' },
              { accessor: 'amountMinor', title: 'Commission', unit: 'money' },
              { accessor: 'kind', title: 'What happened', enumGroup: 'referralEntryKind' },
            ]}
          />
        </Tabs.Panel>
      </Tabs>
    </>
  );
}
